import { deduplicate } from './batcher';
import { PhoneLookupLocalCache } from './cache.local';
import { DssClient } from './dssClient';
import { isValidForLookup, normalizePhone } from './normalize';
import { selectEntity } from './selector';
import {
  CallHistoryItem,
  ENRICHMENT_STATUS,
  EnrichmentUpdate,
  PhoneEnricherOptions,
  EnrichmentStatus,
  Telemetry,
} from './types';
import type { ContactsResolver } from './contacts/contactsResolver';
import { isContactsReady } from './contacts';
import type { DirectorySearchLookup, OutlookLookup } from './types';
import { lookupEntityByDirectorySearch } from './directorySearchFallback';
import { lookupEntityByOutlook } from './outlookLookupFallback';

const DIRECTORY_SEARCH_CONCURRENCY = 4;
const OUTLOOK_LOOKUP_CONCURRENCY = 2;

export class PhoneEnricher {
  private cache: PhoneLookupLocalCache;
  private dssClient: DssClient;
  private contactsResolver?: ContactsResolver;
  private unknownLabel: string;
  private inFlightPhones: Set<string>;
  private telemetry?: Telemetry;
  private enableDirectorySearchFallback: boolean;
  private directorySearchLookup?: DirectorySearchLookup;
  private enableOutlookLookup: boolean;
  private outlookLookup?: OutlookLookup;

  constructor(
    cache: PhoneLookupLocalCache, 
    dssClient: DssClient, 
    options: PhoneEnricherOptions,
    contactsResolver?: ContactsResolver
  ) {
    this.cache = cache;
    this.dssClient = dssClient;
    this.contactsResolver = contactsResolver;
    this.unknownLabel = options.unknownLabel;
    this.inFlightPhones = new Set();
    this.telemetry = options.telemetry;
    this.enableDirectorySearchFallback = options.enableDirectorySearchFallback !== false;
    this.directorySearchLookup = options.directorySearchLookup;
    this.enableOutlookLookup = options.enableOutlookLookup !== false;
    this.outlookLookup = options.outlookLookup;
  }

  private needsEnrichment(item: CallHistoryItem): boolean {
    if (!item.phoneNumber) {
      return false;
    }

    const phone = normalizePhone(item.phoneNumber);
    if (!isValidForLookup(phone)) {
      return false;
    }

    const name = item.name || '';
    const needs = !name || normalizePhone(name) === phone || name === this.unknownLabel;
    return needs;
  }

  private applyEntityUpdates(
    phone: string,
    entity: { displayName: string; entityId: string; provider: string },
    phoneToItemIds: Record<string, string[]>,
    updates: EnrichmentUpdate,
    source: 'cache' | 'api'
  ): void {
    this.cache.set(phone, entity);
    const ids = phoneToItemIds[phone] || [];
    ids.forEach((id) => {
      updates[id] = {
        displayName: entity.displayName,
        entityId: entity.entityId,
        status: ENRICHMENT_STATUS.ENRICHED,
      };
    });
    this.telemetry?.emitSuccess(phone, 0, source);
  }

  private async resolveOutlookMisses(
    phones: string[],
    phoneToItemIds: Record<string, string[]>,
    onUpdate: (updates: EnrichmentUpdate) => void,
    signal?: AbortSignal
  ): Promise<string[]> {
    if (!phones.length) {
      return [];
    }

    const lookup =
      this.outlookLookup ??
      (this.enableOutlookLookup ? lookupEntityByOutlook : undefined);

    if (!lookup) {
      return phones;
    }

    const enrichedUpdates: EnrichmentUpdate = {};
    const stillMissing: string[] = [];

    for (let i = 0; i < phones.length; i += OUTLOOK_LOOKUP_CONCURRENCY) {
      if (signal?.aborted) {
        return phones.slice(i);
      }

      const chunk = phones.slice(i, i + OUTLOOK_LOOKUP_CONCURRENCY);
      const results = await Promise.all(
        chunk.map(async (phone) => ({
          phone,
          entity: await lookup(phone).catch(() => undefined),
        }))
      );

      for (const { phone, entity } of results) {
        if (entity?.displayName) {
          this.applyEntityUpdates(phone, entity, phoneToItemIds, enrichedUpdates, 'api');
        } else {
          stillMissing.push(phone);
        }
      }
    }

    if (Object.keys(enrichedUpdates).length > 0) {
      onUpdate(enrichedUpdates);
    }

    return stillMissing;
  }

  private async resolveDirectorySearchMisses(
    phones: string[],
    phoneToItemIds: Record<string, string[]>,
    onUpdate: (updates: EnrichmentUpdate) => void,
    signal?: AbortSignal,
    resolvedPhones?: Set<string>,
    runOutlookFallback = true
  ): Promise<void> {
    if (!phones.length) {
      return;
    }

    const lookup =
      this.directorySearchLookup ??
      (this.enableDirectorySearchFallback ? lookupEntityByDirectorySearch : undefined);

    if (!lookup) {
      if (runOutlookFallback) {
        const afterOutlook = await this.resolveOutlookMisses(
          phones.filter((phone) => !resolvedPhones?.has(phone)),
          phoneToItemIds,
          onUpdate,
          signal
        );

        if (afterOutlook.length > 0) {
          const missUpdates: EnrichmentUpdate = {};
          for (const phone of afterOutlook) {
            const ids = phoneToItemIds[phone] || [];
            ids.forEach((id) => {
              missUpdates[id] = { status: ENRICHMENT_STATUS.MISS };
            });
            this.telemetry?.emitMiss(phone, 'not_found');
          }
          if (Object.keys(missUpdates).length > 0) {
            onUpdate(missUpdates);
          }
        }
      }
      return;
    }

    const enrichedUpdates: EnrichmentUpdate = {};
    const stillMissing: string[] = [];

    for (let i = 0; i < phones.length; i += DIRECTORY_SEARCH_CONCURRENCY) {
      if (signal?.aborted) {
        return;
      }

      const chunk = phones.slice(i, i + DIRECTORY_SEARCH_CONCURRENCY);
      const results = await Promise.all(
        chunk.map(async (phone) => {
          if (resolvedPhones?.has(phone)) {
            return { phone, entity: undefined };
          }
          return {
            phone,
            entity: await lookup(phone).catch(() => undefined),
          };
        })
      );

      for (const { phone, entity } of results) {
        if (entity?.displayName) {
          resolvedPhones?.add(phone);
          this.applyEntityUpdates(phone, entity, phoneToItemIds, enrichedUpdates, 'api');
        } else if (!resolvedPhones?.has(phone)) {
          stillMissing.push(phone);
        }
      }
    }

    if (Object.keys(enrichedUpdates).length > 0) {
      onUpdate(enrichedUpdates);
    }

    if (stillMissing.length > 0 && runOutlookFallback) {
      const outlookStillMissing = await this.resolveOutlookMisses(
        stillMissing,
        phoneToItemIds,
        onUpdate,
        signal
      );

      if (outlookStillMissing.length > 0) {
        const missUpdates: EnrichmentUpdate = {};
        for (const phone of outlookStillMissing) {
          const ids = phoneToItemIds[phone] || [];
          ids.forEach((id) => {
            missUpdates[id] = { status: ENRICHMENT_STATUS.MISS };
          });
          this.telemetry?.emitMiss(phone, 'outlook_lookup_miss');
        }
        onUpdate(missUpdates);
      }
    }
  }

  private async markUnresolvedMisses(
    phones: string[],
    phoneToItemIds: Record<string, string[]>,
    onUpdate: (updates: EnrichmentUpdate) => void,
    resolvedPhones: Set<string>,
    errorCategory: string
  ): Promise<void> {
    const missUpdates: EnrichmentUpdate = {};
    for (const phone of phones) {
      if (resolvedPhones.has(phone)) {
        continue;
      }
      const ids = phoneToItemIds[phone] || [];
      ids.forEach((id) => {
        missUpdates[id] = { status: ENRICHMENT_STATUS.MISS };
      });
      this.telemetry?.emitMiss(phone, errorCategory);
    }
    if (Object.keys(missUpdates).length > 0) {
      onUpdate(missUpdates);
    }
  }

  private async runDssAndDirectorySearchInParallel(
    phonesToLookup: string[],
    phoneToItemIds: Record<string, string[]>,
    onUpdate: (updates: EnrichmentUpdate) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const resolvedPhones = new Set<string>();

    const runDss = async (): Promise<void> => {
      try {
        await this.dssClient.lookupMany(
          phonesToLookup,
          (progress) => {
            const batchUpdates: EnrichmentUpdate = {};

            for (const phone of progress.phonesInBatch) {
              if (resolvedPhones.has(phone)) {
                this.inFlightPhones.delete(phone);
                continue;
              }

              const entity = selectEntity(phone, progress.result);

              if (entity) {
                resolvedPhones.add(phone);
                this.applyEntityUpdates(phone, entity, phoneToItemIds, batchUpdates, 'api');
              }

              this.inFlightPhones.delete(phone);
            }

            if (Object.keys(batchUpdates).length > 0) {
              onUpdate(batchUpdates);
            }
          },
          signal
        );
      } catch (error) {
        console.error('[PhoneEnricher] DSS enrichment failed', error);
        for (const phone of phonesToLookup) {
          this.inFlightPhones.delete(phone);
        }
      }
    };

    const runDirectorySearch = async (): Promise<void> => {
      await this.resolveDirectorySearchMisses(
        phonesToLookup,
        phoneToItemIds,
        onUpdate,
        signal,
        resolvedPhones,
        false
      );
    };

    await Promise.all([runDss(), runDirectorySearch()]);

    const stillMissing = phonesToLookup.filter((phone) => !resolvedPhones.has(phone));
    if (stillMissing.length === 0) {
      return;
    }

    const outlookStillMissing = await this.resolveOutlookMisses(
      stillMissing,
      phoneToItemIds,
      onUpdate,
      signal
    );

    if (outlookStillMissing.length > 0) {
      await this.markUnresolvedMisses(
        outlookStillMissing,
        phoneToItemIds,
        onUpdate,
        resolvedPhones,
        'outlook_lookup_miss'
      );
    }
  }

  async enrich(
    callHistoryItems: CallHistoryItem[],
    onUpdate: (updates: EnrichmentUpdate) => void,
    signal?: AbortSignal
  ): Promise<void> {
    if (signal?.aborted) {
      return;
    }

    const itemsToEnrich = callHistoryItems.filter((item) => this.needsEnrichment(item));
    
    if (itemsToEnrich.length === 0) {
      return;
    }

    const phoneToItemIds: Record<string, string[]> = {};
    for (const item of itemsToEnrich) {
      const phone = normalizePhone(item.phoneNumber);
      if (!phoneToItemIds[phone]) {
        phoneToItemIds[phone] = [];
      }
      phoneToItemIds[phone].push(item.id);
    }

    const cacheHits: EnrichmentUpdate = {};
    const cacheMisses: string[] = [];
    const staleForRefresh: string[] = [];

    for (const [phone, itemIds] of Object.entries(phoneToItemIds)) {
      if (this.inFlightPhones.has(phone)) {
        continue;
      }

      const cached = this.cache.get(phone);
      if (cached) {
        itemIds.forEach((id) => {
          cacheHits[id] = {
            displayName: cached.displayName,
            entityId: cached.entityId,
            status: ENRICHMENT_STATUS.CACHE_HIT,
          };
        });

        if (this.cache.isExpired(phone)) {
          staleForRefresh.push(phone);
        }

        if (this.telemetry) {
          this.telemetry.emitAttempt(phone, 'hot');
          this.telemetry.emitSuccess(phone, 0, 'cache');
        }
      } else {
        cacheMisses.push(phone);
        if (this.telemetry) {
          this.telemetry.emitAttempt(phone, 'miss');
        }
      }
    }

    if (Object.keys(cacheHits).length > 0) {
      onUpdate(cacheHits);
    }

    // ========================================
    // Check Contacts (NEW) - Personal contacts lookup
    // ========================================
    let remainingMisses = cacheMisses;
    
    if (this.contactsResolver && isContactsReady({ resolver: this.contactsResolver } as any)) {
      const contactsHits: EnrichmentUpdate = {};
      const contactsMisses: string[] = [];
      
      for (const phone of cacheMisses) {
        const contact = this.contactsResolver.resolve(phone);
        
        if (contact) {
          
          const itemIds = phoneToItemIds[phone] || [];
          itemIds.forEach((id) => {
            contactsHits[id] = {
              displayName: contact.displayName,
              entityId: contact.contactId,
              status: ENRICHMENT_STATUS.ENRICHED,
            };
          });
          
          // Cache the contacts result for future lookups
          // Note: cache.set() manages TTL and timestamps internally
          this.cache.set(phone, {
            displayName: contact.displayName,
            entityId: contact.contactId,
            provider: 'webex-contacts',
          });
          
          if (this.telemetry) {
            this.telemetry.emitAttempt(phone, 'warm');
            this.telemetry.emitSuccess(phone, 0, 'cache');
          }
        } else {
          contactsMisses.push(phone);
        }
      }
      
      if (Object.keys(contactsHits).length > 0) {
        onUpdate(contactsHits);
      }
      
      remainingMisses = contactsMisses;
    }

    // ========================================
    // Fallback to DSS for remaining misses
    // ========================================
    const phonesToLookup = deduplicate([...remainingMisses, ...staleForRefresh]);
    
    if (phonesToLookup.length === 0) {
      return;
    }

    phonesToLookup.forEach((phone) => this.inFlightPhones.add(phone));

    if (!this.dssClient.isLookupApiAvailable()) {
      console.warn('[Enrichment] Enricher: DSS lookup API not available');
      for (const phone of phonesToLookup) {
        this.inFlightPhones.delete(phone);
      }
      await this.resolveDirectorySearchMisses(phonesToLookup, phoneToItemIds, onUpdate, signal);
      return;
    }

    const batchStart = Date.now();

    try {
      await this.runDssAndDirectorySearchInParallel(
        phonesToLookup,
        phoneToItemIds,
        onUpdate,
        signal
      );
    } catch (error) {
      console.error('[PhoneEnricher] Enrichment failed', error);
      for (const phone of phonesToLookup) {
        this.inFlightPhones.delete(phone);
      }
      await this.resolveDirectorySearchMisses(phonesToLookup, phoneToItemIds, onUpdate, signal);
    } finally {
      const latency = Date.now() - batchStart;
      this.telemetry?.emitBatch(phonesToLookup.length, latency);
    }
  }

  dispose(): void {
    this.inFlightPhones.clear();
  }
}
